import { forwardRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router"
import {
  Building,
  ChevronsUpDown,
  CheckIcon,
  ArrowLeftRight,
  LogOut,
  ChevronLeft,
  PanelLeft,
  CreditCard,
  Users,
  FolderKanban,
  SwatchBook,
  ChevronDown,
  ChevronRight,
} from "lucide-react"

import {
  Avatar,
  AvatarImage,
  AvatarFallback,
  SidebarMenuButton,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  Button,
  useSidebar,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@f2e/ui/components/base"

import { PermissionGuard } from "@/components/Auth"
import { AlertBox } from "@/components"
import { getProviderBrand } from "@/lib/brand"
import {
  getAvatarName,
  getAvatarBackgroundColor,
  getResourceUrl,
  isDefaultAvatar,
} from "@/lib/utils"
import { Organization, SystemConfig, Permission } from "@/types"
import { useStore } from "@/store"
import { useSysConfig, useToast } from "@/hooks"
import {
  useUpdateProfileApi,
  UpdateProfileRequest,
  useLeaveOrganizationUsersApi,
} from "@/hooks/api"
import { SETTINGS_ENDPOINTS } from "@/constants"

export interface HeadDropdownMenuProps {
  organization: Organization
}

const HeadDropdownMenu = ({ organization }: HeadDropdownMenuProps) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const config = useSysConfig()
  const { alertToast } = useToast()
  const { icon: IconComponent } = getProviderBrand()
  const { open, openMobile, toggleSidebar, isMobile } = useSidebar()
  const avatarUrl = getResourceUrl(
    organization?.avatar ?? "",
    config.serverRoot
  )
  const [userMe, currentOrganization, organizations] = useStore((state) => [
    state.userMe,
    state.currentOrganization,
    state.organizations,
  ])
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false)
  const [isHovering, setIsHovering] = useState(false)
  const [showMobileOrganizationList, setShowMobileOrganizationList] =
    useState(false)

  // APIs
  const { fetchFn: updateProfileApi } = useUpdateProfileApi()
  const { fetchFn: leaveOrganizationUsersApi } = useLeaveOrganizationUsersApi(
    organization.id
  )

  const handleSelectOrganization = (organization: Organization) => {
    const request: UpdateProfileRequest = {
      default_organization_id: organization.id,
    }
    updateProfileApi({
      jsonBody: request,
      callbackFn: (resp) => {
        if (!resp || !resp.data) {
          alertToast({
            title: t("common.headDropdown.errors.switchFailed"),
            description: t("common.headDropdown.errors.tryAgainLater", {
              error: resp?.message,
            }),
          })
          return
        }

        // redirect to home page to update the current organization and permissions
        window.location.href = "/"
      },
    })
  }

  const handleWantToLeaveOrganization = () => {
    console.log("leave organization")
    if (organizations.length === 1) {
      alertToast({
        title: t("common.headDropdown.errors.cannotLeave"),
        description: t("common.headDropdown.errors.needOneOrg"),
      })
      return
    }
    setShowDeleteDialog(true)
  }

  const handleLeaveOrganization = () => {
    if (!userMe) return
    leaveOrganizationUsersApi({
      jsonBody: {
        user_ids: [userMe.id],
      },
      callbackFn: (resp) => {
        if (!resp || !resp.data) return

        setShowDeleteDialog(false)

        // redirect to home page to update the current organization and permissions
        window.location.href = "/"
      },
    })
  }

  const isSidebarOpen = open || openMobile

  return (
    <>
      <div className="flex items-center w-full group-data-[collapsible=icon]:justify-center">
        {isSidebarOpen ? (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 mr-1 shrink-0"
            onClick={toggleSidebar}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            {isHovering ? (
              <ChevronLeft className="h-4 w-4" />
            ) : (
              <PanelLeft className="h-4 w-4" />
            )}
          </Button>
        ) : (
          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-10 w-10 group-data-[collapsible=icon]:h-12 group-data-[collapsible=icon]:w-12"
                  onClick={toggleSidebar}
                >
                  <PanelLeft className="h-5 w-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">
                {t("common.headDropdown.toggleSidebar")}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}

        {isSidebarOpen && (
          <DropdownMenu
            onOpenChange={(nextOpen) => {
              if (!nextOpen) setShowMobileOrganizationList(false)
            }}
          >
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton className="flex-1 h-12 text-xl" asChild>
                <div className="flex items-center">
                  <span
                    className={"w-10 h-10 flex items-center justify-center"}
                  >
                    {isDefaultAvatar(avatarUrl) ? (
                      <IconComponent />
                    ) : (
                      <Avatar className="w-8 h-8 text-sm">
                        <AvatarImage src={avatarUrl} />
                        <AvatarFallback
                          style={{
                            backgroundColor: getAvatarBackgroundColor(
                              organization?.name
                            ),
                          }}
                        >
                          {getAvatarName(organization?.name)}
                        </AvatarFallback>
                      </Avatar>
                    )}
                  </span>
                  <span className="ml-1 font-sans">{organization?.name}</span>
                  <span className="ml-auto">
                    <ChevronsUpDown className="h-5 w-5" />
                  </span>
                </div>
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56">
              <DropdownMenuGroup>
                <MenuItem
                  icon={<Building size={18} />}
                  label={t("common.headDropdown.organizationSettings")}
                  onClick={() =>
                    navigate(SETTINGS_ENDPOINTS.organizationGeneral)
                  }
                />
                <PermissionGuard permission={Permission.OrgAddMembers} disabled>
                  <MenuItem
                    icon={<Users size={18} />}
                    label={t("settings.nav.users")}
                    onClick={() =>
                      navigate(SETTINGS_ENDPOINTS.organizationUsers)
                    }
                  />
                </PermissionGuard>
                <MenuItem
                  icon={<FolderKanban size={18} />}
                  label={t("settings.nav.workspaces")}
                  onClick={() =>
                    navigate(SETTINGS_ENDPOINTS.organizationWorkspaces)
                  }
                />
                <MenuItem
                  icon={<SwatchBook size={18} />}
                  label={t("settings.nav.brands")}
                  onClick={() =>
                    navigate(SETTINGS_ENDPOINTS.organizationBrands)
                  }
                />
                <MenuItem
                  icon={<CreditCard size={18} />}
                  label={t("settings.nav.plan")}
                  onClick={() => navigate(SETTINGS_ENDPOINTS.organizationPlan)}
                />
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                {isMobile ? (
                  <>
                    <DropdownMenuItem
                      aria-expanded={showMobileOrganizationList}
                      onSelect={(event) => {
                        event.preventDefault()
                        setShowMobileOrganizationList((current) => !current)
                      }}
                    >
                      <ArrowLeftRight className="w-4 h-4 mr-2" />
                      <span className="mr-2">
                        {t("common.headDropdown.switchOrganization")}
                      </span>
                      {showMobileOrganizationList ? (
                        <ChevronDown className="ml-auto h-4 w-4" />
                      ) : (
                        <ChevronRight className="ml-auto h-4 w-4" />
                      )}
                    </DropdownMenuItem>
                    {showMobileOrganizationList && currentOrganization && (
                      <div className="max-h-[min(22rem,var(--radix-dropdown-menu-content-available-height))] overflow-y-auto overscroll-contain py-1">
                        <OrganizationMenuItems
                          config={config}
                          currentOrganization={currentOrganization}
                          organizations={organizations}
                          onSelectOrganization={handleSelectOrganization}
                          itemClassName="pl-2"
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger>
                      <ArrowLeftRight className="w-4 h-4 mr-2" />
                      <span className="mr-2">
                        {t("common.headDropdown.switchOrganization")}
                      </span>
                    </DropdownMenuSubTrigger>
                    <DropdownMenuPortal>
                      {currentOrganization && (
                        <SwitchOrganizationSubMenu
                          config={config}
                          currentOrganization={currentOrganization}
                          organizations={organizations}
                          onSelectOrganization={handleSelectOrganization}
                        />
                      )}
                    </DropdownMenuPortal>
                  </DropdownMenuSub>
                )}
              </DropdownMenuGroup>
              <DropdownMenuGroup className="text-red-500">
                <MenuItem
                  icon={<LogOut size={18} />}
                  label={t("common.headDropdown.leaveOrganization")}
                  disabled={currentOrganization?.owner.id === userMe?.id}
                  onClick={handleWantToLeaveOrganization}
                />
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Dialog */}
      <AlertBox
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        title={t("common.headDropdown.leaveDialog.title")}
        description={t("common.headDropdown.leaveDialog.description")}
        onConfirm={handleLeaveOrganization}
        onCancel={(e) => e.stopPropagation()}
      />
    </>
  )
}

const MenuItem = ({
  icon,
  label,
  disabled = false,
  onClick,
}: {
  icon?: React.ReactNode
  label: string
  disabled?: boolean
  onClick?: () => void
}) => {
  return (
    <DropdownMenuItem onClick={onClick} disabled={disabled}>
      {icon}
      <span className={icon ? "ml-2" : ""}>{label}</span>
    </DropdownMenuItem>
  )
}

interface OrganizationMenuItemsProps {
  config: SystemConfig
  currentOrganization: Organization
  organizations: Organization[]
  onSelectOrganization: (organization: Organization) => void
  itemClassName?: string
}

const OrganizationMenuItems = ({
  config,
  currentOrganization,
  organizations,
  onSelectOrganization,
  itemClassName,
}: OrganizationMenuItemsProps) => {
  const { icon: IconComponent } = getProviderBrand()

  return (
    <>
      {organizations.map((org) => (
        <DropdownMenuItem
          className={`min-w-48 max-w-64 flex flex-row space-x-4 ${itemClassName ?? ""}`}
          key={org.id}
          onClick={() => onSelectOrganization(org)}
        >
          {isDefaultAvatar(
            getResourceUrl(org.avatar ?? "", config.serverRoot)
          ) ? (
            <IconComponent className="w-6 h-6" />
          ) : (
            <Avatar className="w-6 h-6">
              <AvatarImage
                src={getResourceUrl(org.avatar ?? "", config.serverRoot)}
              />
              <AvatarFallback
                style={{
                  backgroundColor: getAvatarBackgroundColor(org.name),
                }}
              >
                {org.name.charAt(0)}
              </AvatarFallback>
            </Avatar>
          )}
          <span className="flex-1 max-w-32 overflow-hidden text-ellipsis whitespace-nowrap">
            {org.name}
          </span>
          {org.id === currentOrganization.id && (
            <CheckIcon className="ml-auto h-4 w-4 justify-self-end" />
          )}
        </DropdownMenuItem>
      ))}
    </>
  )
}

interface SwitchOrganizationSubMenuProps {
  config: SystemConfig
  currentOrganization: Organization
  organizations: Organization[]
  onSelectOrganization: (organization: Organization) => void
}

const SwitchOrganizationSubMenu = forwardRef(
  (
    {
      config,
      currentOrganization,
      organizations,
      onSelectOrganization,
    }: SwitchOrganizationSubMenuProps,
    ref: React.ForwardedRef<HTMLDivElement>
  ) => {
    return (
      <DropdownMenuSubContent
        ref={ref}
        className="max-h-[min(22rem,var(--radix-dropdown-menu-content-available-height))] overflow-y-auto overscroll-contain"
      >
        <OrganizationMenuItems
          config={config}
          currentOrganization={currentOrganization}
          organizations={organizations}
          onSelectOrganization={onSelectOrganization}
        />
      </DropdownMenuSubContent>
    )
  }
)

export default HeadDropdownMenu
